import React, { useEffect, useState } from "react";

const DISMISS_KEY = "chess64_install_dismissed_until";

function isInStandaloneMode() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) return;

    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedUntil && Date.now() < dismissedUntil) return;

    const beforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const installed = () => {
      setDeferredPrompt(null);
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", installed);

    if (isIOS()) setVisible(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setVisible(false);
    setShowIOSHint(false);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      // The prompt event is consumed either way and can't be re-fired this
      // session, so hide the banner on dismissal too — otherwise the Install
      // button stays visible but dead.
      setVisible(false);
      setDeferredPrompt(null);
      return;
    }
    if (isIOS()) {
      setShowIOSHint(true);
    }
  };

  if (!visible) return null;

  const btnWrap = {
    position: "fixed",
    bottom: 16,
    right: 16,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    background: "#1a2a7c",
    border: "3px solid #ffd700",
    borderRadius: 8,
    boxShadow: "3px 4px 0 #0a1040",
    padding: "8px 10px 8px 14px",
    fontFamily: "'Space Grotesk', sans-serif",
  };
  const btnText = { color: "#fff", fontSize: 13, fontWeight: 600, marginRight: 10 };
  const installBtn = {
    background: "#ffd700",
    color: "#0d1654",
    border: "none",
    padding: "6px 12px",
    fontWeight: 700,
    fontSize: 13,
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: "inherit",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };
  const closeBtn = {
    background: "transparent",
    color: "rgba(255,255,255,0.6)",
    border: "none",
    fontSize: 18,
    marginLeft: 8,
    cursor: "pointer",
    lineHeight: 1,
    padding: "0 4px",
  };

  const modalWrap = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.7)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };
  const modalBox = {
    background: "#162060",
    border: "3px solid #ffd700",
    borderRadius: 8,
    boxShadow: "3px 4px 0 #0a1040",
    padding: 20,
    maxWidth: 360,
    color: "#fff",
    fontFamily: "'Space Grotesk', sans-serif",
    lineHeight: 1.5,
  };
  const modalTitle = { color: "#ffd700", fontSize: 16, fontWeight: 700, margin: "0 0 10px", textTransform: "uppercase" };

  return (
    <>
      <div style={btnWrap}>
        <span style={btnText}>Install Chess-64</span>
        <button style={installBtn} onClick={handleInstall}>Install</button>
        <button style={closeBtn} onClick={dismiss} aria-label="Dismiss">×</button>
      </div>

      {showIOSHint && (
        <div style={modalWrap} onClick={() => setShowIOSHint(false)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={modalTitle}>Install on iPhone</h3>
            <p style={{ margin: "0 0 10px" }}>
              Safari doesn't let us trigger installation directly. It only takes a couple of taps:
            </p>
            <ol style={{ margin: "0 0 14px 20px", padding: 0 }}>
              <li>Tap the <strong>Share</strong> button at the bottom of Safari (square with an arrow pointing up).</li>
              <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
              <li>Tap <strong>Add</strong> in the top right.</li>
            </ol>
            <button style={installBtn} onClick={() => setShowIOSHint(false)}>Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
